import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Settings, Box, CheckCircle, BarChart2 } from "lucide-react";

const ManufacturingUseCase = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="min-h-screen bg-white">
        <div className="relative bg-[#0077bb] py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative">
              <h1 className="text-4xl font-bold text-white mb-6">
                Manufacturing & Quality Control
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl">
                Streamline production processes, ensure quality standards, and maintain efficient operations with digital manufacturing solutions.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <img
                src="https://erpnext.com/files/production-plan-cover-small.jpeg"
                alt="Manufacturing production planning and operations"
                className="rounded-lg shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Enhance Manufacturing Operations
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Transform your manufacturing processes with digital solutions that improve efficiency and quality:
              </p>
              <ul className="space-y-4 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Quality control and inspection management
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Equipment maintenance tracking
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Production process optimization
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Safety compliance documentation
                </li>
              </ul>
            </div>
          </div>

          <section className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-center mb-12">
                Key Features
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                    <Settings className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Process Control</h3>
                  <p className="text-gray-600">
                    Monitor and optimize manufacturing processes in real-time.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Quality Assurance</h3>
                  <p className="text-gray-600">
                    Ensure consistent product quality with digital inspection tools.
                  </p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                    <BarChart2 className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Performance Analytics</h3>
                  <p className="text-gray-600">
                    Track KPIs and optimize production efficiency.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div className="w-full bg-[#F8F9FA]">
        <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Common Use Cases
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-sm border border-gray-100">
              <h3 className="text-2xl font-semibold mb-6">Production Management</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Process control monitoring
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Equipment maintenance tracking
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Production scheduling
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Resource allocation
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-sm border border-gray-100">
              <h3 className="text-2xl font-semibold mb-6">Quality Control</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Product inspections
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Quality audits
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Defect tracking
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  Compliance reporting
                </li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <section className="py-20">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-6">
                Ready to Transform Your Manufacturing Process?
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Join leading manufacturers who have optimized their operations with our digital solutions.
              </p>
              <div className="space-x-4">
                <Link to="/request-demo">
                  <Button className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                    Request Demo
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border border-blue-600 text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
                    Contact Sales
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default ManufacturingUseCase;
